import { useEffect, useRef, useState } from 'preact/hooks'
import {
  RefreshCcw, Plus, Upload, Search, AlertCircle, Globe, RotateCcw, Loader2,
  ChevronLeft, ChevronRight, X, CheckCircle2, Info,
} from 'lucide-preact'
import { useDomainSuppressions } from '@/hooks/useDomainSuppressions'
import { useToast } from '@/hooks/useToast'
import type { DomainSuppressionImportResultDto } from '@/types/domainSuppressions'

interface DomainSuppressionListProps {
  clientCompanyId: string
  /** Reports the live total up to the parent screen for the tab count. */
  onTotalChange?: (total: number) => void
}

// Format-only guard; the server is authoritative (it also rejects free/public domains). A registrable
// domain: labels of alphanumerics/hyphens then an alphabetic TLD. Mirrors the API's DomainShapeRegex.
const DOMAIN_PATTERN = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/
// Mirror the server's per-upload cap so we fail loudly client-side instead of round-tripping a 422.
const MAX_IMPORT = 2000

// Reduce a raw token to a bare, lower-cased domain: strip a scheme/path, take the part after '@' when an
// email is pasted, drop a leading "www." and a trailing dot. Mirrors the server normalization so the
// client sends clean values. Returns null when nothing usable remains.
function normalizeDomainToken(raw: string): string | null {
  let value = raw.trim().toLowerCase().replace(/^["']|["']$/g, '')
  if (!value) return null
  const scheme = value.indexOf('://')
  if (scheme >= 0) value = value.slice(scheme + 3)
  const at = value.lastIndexOf('@')
  if (at >= 0) value = value.slice(at + 1)
  const cut = value.search(/[/?#]/)
  if (cut >= 0) value = value.slice(0, cut)
  if (value.startsWith('www.')) value = value.slice(4)
  value = value.replace(/\.+$/, '')
  return value || null
}

// Pull every valid, de-duplicated domain out of a pasted/CSV blob. Headers and other columns simply don't
// match the pattern and are ignored — the domain can sit in any column, and pasted emails reduce to their
// domain.
function extractDomains(text: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of text.split(/[\s,;]+/)) {
    const domain = normalizeDomainToken(raw)
    if (!domain || !DOMAIN_PATTERN.test(domain)) continue
    if (seen.has(domain)) continue
    seen.add(domain)
    out.push(domain)
  }
  return out
}

// Human-facing label for a stored Source enum description (display only; the API value is unchanged).
function sourceLabel(source: string): string {
  switch (source) {
    case 'Admin Action': return 'Added by admin'
    default: return source // 'Import'
  }
}

function sourceBadgeClass(source: string): string {
  switch (source) {
    case 'Import': return 'text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/40 ring-sky-600/20'
    case 'Admin Action': return 'text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-950/40 ring-violet-600/20'
    default: return 'text-slate-600 dark:text-slate-300 ring-slate-300/60 dark:ring-slate-600/60'
  }
}

function ImportChip({ label, value, muted }: { label: string; value: number; muted?: boolean }) {
  return (
    <span class={`rounded-md bg-white/70 dark:bg-slate-900/50 px-2 py-0.5 text-xs font-medium ring-1 ring-slate-200 dark:ring-slate-700 ${muted ? 'text-slate-500 dark:text-slate-400' : 'text-slate-700 dark:text-slate-200'}`}>
      {value} {label}
    </span>
  )
}

export function DomainSuppressionList({ clientCompanyId, onTotalChange }: DomainSuppressionListProps) {
  const {
    items, total, loading, error, setSearch, page, setPage, pageSize, reload,
    addManual, remove, importList,
  } = useDomainSuppressions(clientCompanyId)
  const { showError, showSuccess } = useToast()

  // Keep the parent screen's tab count in sync with the live total.
  useEffect(() => { onTotalChange?.(total) }, [total, onTotalChange])

  // Debounced search box → hook search (resetting to the first page).
  const [searchInput, setSearchInput] = useState('')
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setSearch(searchInput.trim()) }, 300)
    return () => clearTimeout(t)
  }, [searchInput, setPage, setSearch])

  // Inline add form.
  const [showAddForm, setShowAddForm] = useState(false)
  const [newDomain, setNewDomain] = useState('')
  const [newReason, setNewReason] = useState('')
  const [submittingAdd, setSubmittingAdd] = useState(false)
  const normalizedNewDomain = normalizeDomainToken(newDomain) ?? ''
  const isNewDomainValid = DOMAIN_PATTERN.test(normalizedNewDomain)

  // Per-row + import busy state. A Set so concurrent removals each track independently.
  const [pendingDomains, setPendingDomains] = useState<Set<string>>(new Set())
  const [importing, setImporting] = useState(false)
  const [importSummary, setImportSummary] = useState<{ result: DomainSuppressionImportResultDto; fileName: string } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const from = total === 0 ? 0 : page * pageSize + 1
  const to = Math.min(total, (page + 1) * pageSize)
  const canPrev = page > 0
  const canNext = (page + 1) * pageSize < total

  const openAddForm = () => { setNewDomain(''); setNewReason(''); setShowAddForm(true) }

  const handleAdd = async () => {
    if (!isNewDomainValid) {
      showError('Invalid domain', 'Enter a valid domain, e.g. competitor.com.')
      return
    }
    setSubmittingAdd(true)
    const result = await addManual(normalizedNewDomain, newReason)
    setSubmittingAdd(false)
    if (!result.ok) {
      if (!result.handled) showError('Could not block domain', result.error || 'Unknown error')
      return
    }
    showSuccess('Domain blocked', `${normalizedNewDomain} and its subdomains won't receive this company's marketing.`)
    setShowAddForm(false)
    setNewDomain('')
    setNewReason('')
  }

  const handleRemove = async (domain: string) => {
    setPendingDomains(prev => new Set(prev).add(domain))
    const result = await remove(domain)
    setPendingDomains(prev => { const next = new Set(prev); next.delete(domain); return next })
    if (!result.ok) {
      if (!result.handled) showError('Could not remove domain', result.error || 'Unknown error')
      return
    }
    showSuccess('Domain removed', `${domain} can receive marketing again.`)
  }

  const handleFile = async (file: File) => {
    setImportSummary(null)
    const text = await file.text()
    const domains = extractDomains(text)
    if (domains.length === 0) {
      showError('No domains found', 'That file had no valid domains.')
      return
    }
    if (domains.length > MAX_IMPORT) {
      showError('Too many domains', `Found ${domains.length}. Up to ${MAX_IMPORT} per upload — please split the file.`)
      return
    }
    setImporting(true)
    const res = await importList(domains)
    setImporting(false)
    if (!res.ok || !res.result) {
      if (!res.handled) showError('Import failed', res.error || 'Unknown error')
      return
    }
    setImportSummary({ result: res.result, fileName: file.name })
    const updated = res.result.Reactivated
    showSuccess('Import complete', `${res.result.Added} added${updated ? `, ${updated} reactivated` : ''}.`)
  }

  const onFileSelected = (e: Event) => {
    const input = e.target as HTMLInputElement
    const file = input.files?.[0]
    if (file) void handleFile(file)
    input.value = '' // allow re-selecting the same file
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-prose leading-relaxed">
            Whole domains this company will never send marketing to. Upload a competitor or do-not-contact
            list; any address at these domains is skipped.
          </p>
          <p className="flex items-start gap-1.5 mt-2 text-xs text-slate-400 dark:text-slate-500 max-w-prose">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary-500" />
            <span>
              Blocking <span className="font-medium text-slate-500 dark:text-slate-400">competitor.com</span> also
              blocks its subdomains (mail.competitor.com). Free providers like gmail.com and outlook.com can't be blocked.
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={reload}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
          >
            <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import CSV
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            className="hidden"
            onChange={onFileSelected}
          />
          {!showAddForm && (
            <button
              onClick={openAddForm}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" /> Add domain
            </button>
          )}
        </div>
      </div>

      {/* Import result summary */}
      {importSummary && (
        <div className="rounded-xl bg-primary-50 dark:bg-primary-950/40 ring-1 ring-primary-200 dark:ring-primary-900 px-4 py-3" aria-live="polite">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 mt-0.5 text-primary-600 dark:text-primary-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-primary-800 dark:text-primary-200 break-all">
                Imported {importSummary.result.Total} domains from {importSummary.fileName}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <ImportChip label="added" value={importSummary.result.Added} />
                <ImportChip label="re-activated" value={importSummary.result.Reactivated} />
                <ImportChip label="already blocked" value={importSummary.result.AlreadyBlocked} />
                <ImportChip label="skipped (free)" value={importSummary.result.Skipped} muted />
                <ImportChip label="invalid" value={importSummary.result.Invalid} muted />
                <ImportChip label="duplicate" value={importSummary.result.Duplicate} muted />
              </div>
            </div>
            <button
              onClick={() => setImportSummary(null)}
              aria-label="Dismiss import summary"
              className="text-primary-600/70 dark:text-primary-400/70 hover:opacity-100"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Inline add form */}
      {showAddForm && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1 min-w-0 space-y-1">
              <label htmlFor="new-suppression-domain" className="block text-xs font-medium text-slate-700 dark:text-slate-200">
                Domain
              </label>
              <input
                id="new-suppression-domain"
                type="text"
                autoFocus
                value={newDomain}
                onInput={e => setNewDomain((e.target as HTMLInputElement).value)}
                onKeyDown={e => { if (e.key === 'Enter' && isNewDomainValid && !submittingAdd) void handleAdd() }}
                placeholder="competitor.com"
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex-1 min-w-0 space-y-1">
              <label htmlFor="new-suppression-domain-reason" className="block text-xs font-medium text-slate-700 dark:text-slate-200">
                Reason <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <input
                id="new-suppression-domain-reason"
                type="text"
                value={newReason}
                onInput={e => setNewReason((e.target as HTMLInputElement).value)}
                placeholder="e.g. competitor"
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => { setShowAddForm(false); setNewDomain(''); setNewReason('') }}
                className="px-3 py-2 text-sm rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { void handleAdd() }}
                disabled={!isNewDomainValid || submittingAdd}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {submittingAdd && <Loader2 className="w-4 h-4 animate-spin" />} Add
              </button>
            </div>
          </div>
          {newDomain.trim() && !isNewDomainValid && (
            <p className="text-xs text-red-600 dark:text-red-400">Enter a valid domain, e.g. competitor.com.</p>
          )}
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="search"
          value={searchInput}
          onInput={e => setSearchInput((e.target as HTMLInputElement).value)}
          placeholder="Search domains…"
          aria-label="Search blocked domains"
          className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 pl-9 pr-3 py-2 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {error && (
        <div className="flex items-start gap-2 p-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {loading && items.length === 0 && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-12 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 animate-pulse" />
          ))}
        </div>
      )}

      {!loading && items.length === 0 && !error && (
        <div className="px-8 py-12 text-center border border-dashed rounded-xl border-slate-300 dark:border-slate-700">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
            <Globe className="w-6 h-6 text-slate-400" />
          </div>
          <h3 className="mt-4 text-sm font-medium text-slate-900 dark:text-white">
            No blocked domains{searchInput.trim() ? ' match your search' : ' yet'}
          </h3>
          {!searchInput.trim() && (
            <>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
                Add a domain manually, or import a competitor list to exclude whole domains from this company's marketing.
              </p>
              <div className="mt-5 flex items-center justify-center gap-2">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <Upload className="w-4 h-4" /> Import CSV
                </button>
                <button
                  onClick={openAddForm}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700"
                >
                  <Plus className="w-4 h-4" /> Add domain
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto rounded-xl ring-1 ring-slate-200 dark:ring-slate-800">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                <th scope="col" className="px-4 py-3">Domain</th>
                <th scope="col" className="px-4 py-3">Source</th>
                <th scope="col" className="px-4 py-3">Added</th>
                <th scope="col" className="px-4 py-3">Added by</th>
                <th scope="col" className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-900">
              {items.map(entry => {
                const isPending = pendingDomains.has(entry.Domain)
                return (
                  <tr key={entry.Id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-white break-all">
                      <span className="inline-flex items-center gap-2">
                        <Globe className="w-4 h-4 shrink-0 text-slate-400" />
                        {entry.Domain}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${sourceBadgeClass(entry.Source)}`}>
                        {sourceLabel(entry.Source)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400 whitespace-nowrap" title={new Date(entry.CreatedUtc).toLocaleString()}>
                      {new Date(entry.CreatedUtc).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400 break-all">{entry.CreatedByName || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => { void handleRemove(entry.Domain) }}
                        disabled={isPending}
                        aria-label={`Remove ${entry.Domain}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg text-slate-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                      >
                        {isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Remove
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between gap-4 text-sm text-slate-500 dark:text-slate-400">
          <span>{from}–{to} of {total}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => canPrev && setPage(page - 1)}
              disabled={!canPrev || loading}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <button
              onClick={() => canNext && setPage(page + 1)}
              disabled={!canNext || loading}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:hover:bg-transparent"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
